import React, { useContext, useEffect, useRef } from "react";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { SelectDropDown } from "components/DesignSystem/SelectDropDown/SelectDropDown";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import {
  ADD_TASK_CLICKED,
  TASK_CREATED,
  TASK_CREATION_FAILED,
} from "constants/analyticsEvents";
import dayjs from "dayjs";
import { Form, Formik, useFormikContext, Field } from "formik";
import {
  addAdhocTaskSchema,
  addTaskSchema,
} from "formValidations/addTaskSchema";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { parse, stringify } from "qs";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ArrowLeft from "static/images/BlackArrowLeft.svg";
import CalendarBlank from "static/images/CalendarBlank.svg";
import CoinVertical from "static/images/CoinVertical.svg";
import InfoGray from "static/images/InfoGray.svg";
import {
  UpdatedDeadlineResponse,
  useCreateTaskMutation,
  useGetSingleTaskTemplatesQuery,
  useLazyGetUpdatedDeadlinesQuery,
} from "store/apis/getTemplates";
import "../../../static/styles/components/addTask.css";
import { BackendError } from "types/utils/error";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  BundleToMetadata,
  QuarterlyAdvanceTaxMetadata,
  useAddProductToCartMutation,
  useGetDeletedCartItemQuery,
  YearlyAdvanceTaxMetadata,
} from "store/apis/products";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { CART_ITEM_DELETION_REASON_DICT } from "constants/cartItemDeletionReasonsDict";
import {
  CART_ITEM_STATUS,
  SKIP_PAYMENT_ADD_TO_CART,
  SKIP_PAYMENT_INVOICE_LATER,
  SKIP_PAYMENT_ZERO_ENTRY,
} from "constants/cart";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { MultiValue, SingleValue } from "react-select";
import {
  QUARTERLY_ADVANCE_TAX,
  STATE_FILING,
  YEARLY_ADVANCE_TAX,
} from "types/Models/services";
import { AdvanceTaxDeadline } from "components/AddProductToCart/AdvanceTaxDeadline";
import { AdvanceQuarterlyTaxPrice } from "components/AddProductToCart/AdvanceQuarterlyTaxPrice";
import { EntityLabel } from "components/AddProductToCart/AddProductToCart";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { BaseTaskTemplate } from "types/Models/taskTemplate";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { YYYY_MM_DD } from "constants/date";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { PriceInput } from "components/PriceInput/PriceInput";
import { ADHOC_SEASON_MIN_YEAR } from "constants/adhocTaskIdentifier";
import { getSeasonsFrom } from "utils/getAllSeason";

type DisclaimerCardProps = {
  children: React.ReactNode;
};

type TaskFormData = {
  default_task_template?: boolean;
  seasons?: number[];
  states?: { name: string; state_id: string }[];
  description: string;
  approx_deadline?: string;
  approx_price?: string;
  government_fee?: string;
  title?: string;
  product_content_type_id: number | null;
  quarters?: string[];
  base_task_key?: string;
  metadata?:
    | YearlyAdvanceTaxMetadata
    | QuarterlyAdvanceTaxMetadata
    | { fields: BundleToMetadata[] };
};

type TaskFormProps = {
  data: TaskFormData;
  closeModal: () => void;
  disableLaunch?: boolean;
  templateId: string;
  errors: Record<string, string>;
};

type AddTaskEntityModalProps = {
  to: string;
  redirectPathAfterCreate?: string;
  taskTemplateId?: string;
  handleClose?: () => void;
  disableLaunch?: boolean;
  handleFormSubmit?: (values: any) => void;
};

const DisclaimerCard = ({ children }: DisclaimerCardProps) => {
  return (
    <span className="t-rounded-lg t-bg-yellow-10 t-p-3 t-text-body-sm">
      <span className="t-font-medium">Disclaimer: </span>
      {children}
    </span>
  );
};

const Disclaimer = ({
  updatedDeadlines,
}: {
  updatedDeadlines: UpdatedDeadlineResponse | undefined;
}) => {
  const { is_fiscal, filing7004_filed } = updatedDeadlines || {};

  if (filing7004_filed && is_fiscal) {
    return (
      <DisclaimerCard>
        Since you are a fiscal year company and filed 7004, this deadline has
        been changed accordingly.
      </DisclaimerCard>
    );
  }

  if (is_fiscal) {
    return (
      <DisclaimerCard>
        Since you are a fiscal year company, this deadline has been changed
        accordingly.
      </DisclaimerCard>
    );
  }

  if (filing7004_filed) {
    return (
      <DisclaimerCard>
        Since you have filed 7004, this deadline has been extended by 6 months.
      </DisclaimerCard>
    );
  }

  return null;
};

const DeletedCartItemInfo = (props: {
  base_task_template_id: string;
  season: string;
  state_id: string;
  entity: string;
  product_content_type_id: number;
}) => {
  let validated = false;

  try {
    addTaskSchema.validateSync(props);
    validated = true;
  } catch (error) {}

  const { data: deletedItem } = useGetDeletedCartItemQuery(
    {
      payload: {
        product_id: props.base_task_template_id,
        season: props.season,
        state_id: props.state_id,
        entity_id: props.entity,
        product_content_type_id: props.product_content_type_id,
      },
      entityId: props.entity,
    },
    {
      skip: !props.entity || !validated,
    }
  );

  if (
    deletedItem?.delete_reason &&
    deletedItem?.status === CART_ITEM_STATUS.ARCHIVED
  ) {
    return (
      <div className="t-mb-4">
        <Banner
          type="error"
          description={
            <span className="t-text-red-70">
              Customer has marked this filing as{" "}
              {CART_ITEM_DELETION_REASON_DICT[deletedItem.delete_reason]}.
            </span>
          }
        />
      </div>
    );
  }

  return null;
};

export const TaskForm = ({
  data,
  closeModal,
  disableLaunch = false,
  templateId,
  errors = {},
}: TaskFormProps) => {
  const { entities } = useCurrentGroupContext();
  const { values, setFieldValue } = useFormikContext<any>();
  const [getUpdatedDeadlines, { data: updatedDeadlines }] =
    useLazyGetUpdatedDeadlinesQuery();
  const isCustomDeadline =
    data?.base_task_key === QUARTERLY_ADVANCE_TAX ||
    data?.base_task_key === YEARLY_ADVANCE_TAX;
  const quarterData =
    Array.isArray(data.metadata) &&
    (data.metadata as QuarterlyAdvanceTaxMetadata)?.find(
      (item) => item.value === values.quarter
    );
  const { isCustomer } = useRoleBasedView();

  const { updated_deadline } = updatedDeadlines || {};
  const { season, entity } = values;
  const skipWithInvoiceRef = useRef<HTMLDivElement | null>(null);
  const skipPaymentRef = useRef<HTMLDivElement | null>(null);

  const canUpdateDeadline = !isCustomer && data?.base_task_key === STATE_FILING;
  const firstErrorField = Object.keys(errors)[0];
  const isBookkeepingCatchup =
    templateId === process.env.PUBLIC_BASE_TASK_TEMPLATE_ID_BOOKKEEPING_CATCHUP;
  const validSeasons = data.seasons?.filter((year) => Boolean(year)) || [];

  let availableSeasons = validSeasons;

  if (validSeasons.length === 0 && isBookkeepingCatchup) {
    availableSeasons = getSeasonsFrom(2021, dayjs().year() + 1);
  }

  const seasonOptions = availableSeasons.map((year) => ({
    label: year.toString(),
    value: year.toString(),
  }));

  useEffect(() => {
    if (firstErrorField === "skipPaymentMode" && skipPaymentRef.current) {
      skipPaymentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [firstErrorField]);

  useEffect(() => {
    if (
      values.skipPaymentMode === SKIP_PAYMENT_INVOICE_LATER &&
      skipWithInvoiceRef.current
    ) {
      skipWithInvoiceRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [values.skipPaymentMode]);

  useEffect(() => {
    const getDeadlines = async () => {
      try {
        await getUpdatedDeadlines({
          baseTaskTemplateId: templateId,
          season,
          entity,
        }).unwrap();
      } catch (e) {}
    };

    if (season && entity && templateId) {
      getDeadlines();
    }
  }, [season, entity, templateId, getUpdatedDeadlines]);

  return (
    <div className="add-edit-form t-flex t-flex-col t-gap-6">
      <DeletedCartItemInfo
        {...{
          ...values,
          product_content_type_id: data.product_content_type_id,
        }}
      />
      <div className="t-grid t-w-full t-grid-cols-2 t-gap-4">
        <Combobox
          components={{
            ClearIndicator: () => null,
          }}
          menuPortalTarget={document.body}
          name="entity"
          withForm
          label="Entity"
          options={entities?.map((entity) => ({
            label: (
              <EntityLabel
                name={entity.name}
                countryCode={entity.country_code}
              />
            ),
            value: entity.uuid,
          }))}
        />
        {(!data?.default_task_template || isBookkeepingCatchup) && (
          <Combobox
            components={{
              ClearIndicator: () => null,
            }}
            menuPortalTarget={document.body}
            name="season"
            withForm
            label="Season"
            options={seasonOptions}
          />
        )}
        {data?.states && (
          <Combobox
            components={{
              ClearIndicator: () => null,
            }}
            menuPortalTarget={document.body}
            name="state_id"
            withForm
            label="State"
            options={data.states.map((state) => ({
              label: state.name,
              value: state.state_id,
            }))}
            styles={{
              menuList: (base) => ({
                ...base,
                maxHeight: "180px",
                overflowY: "auto",
              }),
            }}
          />
        )}
        {data?.quarters && data?.quarters.length > 0 && (
          <Combobox
            required
            menuPortalTarget={document.body}
            name="quarter"
            withForm
            label="Quarter"
            value={
              Boolean(values.quarter)
                ? {
                    value: values.quarter,
                    label: values.quarter,
                  }
                : null
            }
            options={data?.quarters?.map((quarter: string, index) => ({
              value: quarter,
              label: quarter,
            }))}
          />
        )}
        {canUpdateDeadline && (
          <DateField
            name="deadline"
            dateProps={{
              label: "Deadline",
              required: true,
              onDateChange: (date: Date) =>
                setFieldValue("deadline", dayjs(date).format(YYYY_MM_DD)),
              rightComponent: (
                <CalendarIcon className="t-text-text-30 -t-mt-0.5" />
              ),
            }}
          />
        )}
        {data?.metadata?.hasOwnProperty("fields") &&
          "fields" in data.metadata &&
          data?.metadata?.fields[0]?.type !== undefined && (
            <>
              {data?.metadata.fields.map((data) => {
                if (data.type === "bundle_checkbox") {
                  return (
                    <div
                      className="t-space-y-2 t-col-span-2"
                      key={data.base_task_template_id}
                    >
                      <div className="t-text-body-sm t-text-text-30">
                        {data.title}
                      </div>
                      <Checkbox
                        name="bundle"
                        onChange={(e) =>
                          setFieldValue("bundle", e.target.checked)
                        }
                        label={data.label}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </>
          )}
      </div>
      <span>
        <div className="t-text-body-sm t-text-text-30">About</div>
        <span
          className="t-text-text-100 t-text-body-sm"
          dangerouslySetInnerHTML={{
            __html: data.description,
          }}
        ></span>
      </span>
      <div className="t-grid t-grid-cols-2 t-gap-3">
        {isCustomDeadline && (
          <div className="t-flex-1">
            <div className="t-text-body-sm t-text-neutral">
              Typical Deadlines
            </div>
            <AdvanceTaxDeadline
              metadata={
                data.metadata as
                  | YearlyAdvanceTaxMetadata
                  | QuarterlyAdvanceTaxMetadata
              }
              isYearly={data?.base_task_key === YEARLY_ADVANCE_TAX}
              selectedQuarter={values.quarter}
            />
          </div>
        )}
        {data?.approx_deadline && !isCustomDeadline && (
          <div className="t-flex-1">
            <div className="t-text-body-sm t-text-neutral">
              Typical Deadlines
            </div>
            <div className="t-text-subtext t-text-100 t-font-bold">
              {updated_deadline ? (
                <>{dayjs(updated_deadline).format("Do MMMM YYYY")}</>
              ) : (
                <>{dayjs(data.approx_deadline).format("Do MMMM")} each year</>
              )}
            </div>
          </div>
        )}
        <div className="t-mt-1 t-flex-1">
          <div className="t-text-body-sm t-text-neutral">Price</div>
          {data?.base_task_key === QUARTERLY_ADVANCE_TAX && quarterData ? (
            <AdvanceQuarterlyTaxPrice quarterData={quarterData} />
          ) : (
            <div className="t-text-subtext t-text-100 t-font-bold">
              {data.approx_price === "0" ? "Free" : "$" + data.approx_price}
            </div>
          )}
        </div>
        {Boolean(data?.government_fee) && (
          <div className="t-mt-1 t-flex-1">
            <div className="t-text-body-sm t-text-neutral">Government Fee</div>
            <div className="t-text-subtext t-text-100 t-font-bold">
              {data.government_fee === "0" ? "Free" : "$" + data.government_fee}
            </div>
          </div>
        )}
      </div>
      <Disclaimer updatedDeadlines={updatedDeadlines} />
      {!isCustomer && (
        <Field name="skipPaymentMode">
          {({ field, meta }: any) => (
            <>
              <Radio.Root
                onValueChange={(value) =>
                  setFieldValue("skipPaymentMode", value)
                }
                required
                ref={skipPaymentRef}
              >
                <Label required htmlFor="skipPaymentMode">
                  Choose how you want to launch this filing
                </Label>
                <Radio.Content>
                  <Field name="skipPaymentMode">
                    {({ field, meta }: any) => (
                      <>
                        <Radio.Item
                          asChild
                          value={SKIP_PAYMENT_ADD_TO_CART}
                          checked={field.value === SKIP_PAYMENT_ADD_TO_CART}
                        >
                          <div className="t-text-body-sm t-text-text-100">
                            Add to cart
                          </div>
                        </Radio.Item>
                        <Radio.Item
                          asChild
                          value={SKIP_PAYMENT_INVOICE_LATER}
                          checked={field.value === SKIP_PAYMENT_INVOICE_LATER}
                        >
                          <div className="t-text-body-sm t-text-text-100">
                            Skip the payment & create an invoice for later
                          </div>
                        </Radio.Item>
                        <Radio.Item
                          asChild
                          value={SKIP_PAYMENT_ZERO_ENTRY}
                          checked={field.value === SKIP_PAYMENT_ZERO_ENTRY}
                        >
                          <div className="t-text-body-sm t-text-text-100">
                            Skip payment & record a $0 entry
                          </div>
                        </Radio.Item>
                        {meta.touched && meta.error && (
                          <div className="t-mt-1.5 t-text-body-sm t-text-red">
                            {meta.error}
                          </div>
                        )}
                      </>
                    )}
                  </Field>
                </Radio.Content>
              </Radio.Root>
            </>
          )}
        </Field>
      )}

      {values.skipPaymentMode === SKIP_PAYMENT_INVOICE_LATER && (
        <div className="t-mt-2 t-space-y-4" ref={skipWithInvoiceRef}>
          <div className="t-text-subtitle-lg t-text-text-100">
            Invoice Details
          </div>
          <PriceInput name="amount" label="Amount" type="number" required />
          <div className="t-flex t-justify-between t-w-full t-gap-4">
            <div className="t-flex-1">
              <Combobox
                required
                isDisabled
                options={[{ label: "One-time", value: "One-time" }]}
                value={{
                  label: "One-time",
                  value: "One-time",
                }}
                label="Invoice type"
                block
                fullWidth
              />
            </div>
            <div className="t-flex-1">
              <Combobox
                required
                isDisabled
                options={[{ label: data.title, value: data.title || "" }]}
                value={{
                  label: data.title,
                  value: data.title || "",
                }}
                label="Task"
                block
                fullWidth
              />
            </div>
          </div>
          <TextInput
            block
            name="description"
            label="Description"
            placeholder="Enter description"
          />
        </div>
      )}
    </div>
  );
};

export default function AddTaskEntityModal({
  to,
  redirectPathAfterCreate = "",
  taskTemplateId = "",
  handleClose,
  disableLaunch = false,
  handleFormSubmit,
}: AddTaskEntityModalProps) {
  const { trackEvent } = useAnalytics();
  const { alertToast, successToast } = useToast();
  const query = useQuery();
  const { entities, uuid: groupId } = useCurrentGroupContext();
  const { authtoken } = useContext(authContext);
  const { templateId } = useParams<{ templateId: string }>();
  const history = useHistory();
  const { email } = authtoken;
  const queryEntity = query.get("entity");
  const querySeason = query.get("season");
  const location = useLocation();
  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const { isCustomer } = useRoleBasedView();
  const search = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const finalTemplateId = templateId || taskTemplateId;

  const {
    data,
    isError: getTaskTemplatesIsError,
    error,
  } = useGetSingleTaskTemplatesQuery(
    { templateId: finalTemplateId },
    {
      skip: !finalTemplateId,
    }
  );

  const [createTask, { isLoading }] = useCreateTaskMutation();
  const [addProductToCart, { isLoading: addingProductToCart }] =
    useAddProductToCartMutation();

  const closeModal = () => {
    if (handleClose) {
      handleClose();
      return;
    }
    history.replace(to);
  };

  if (getTaskTemplatesIsError && (error as BackendError).status === 402) {
    closeModal();
  }

  const getDeadlineDate = (deadline?: string, approx_deadline?: string) => {
    const date = deadline || approx_deadline;
    return date ? { deadline_date: dayjs(date).format(YYYY_MM_DD) } : {};
  };

  const handleSkipPaymentAddToCart = async (values: any) => {
    const {
      title,
      product_content_type_id,
      uuid,
      approx_deadline,
      approx_price,
      metadata,
    } = data || {};

    const {
      state_id,
      season,
      base_task_template_id,
      entity,
      quarter,
      bundle,
      deadline,
    } = values;
    if (groupId && uuid && product_content_type_id) {
      try {
        const taskName = title ? { task_name: title } : {};
        const bundleMetadata =
          metadata?.hasOwnProperty("fields") && "fields" in metadata
            ? metadata.fields.find(
                (data) => (data as BundleToMetadata).type === "bundle_checkbox"
              )
            : null;
        const bundleTo = bundle
          ? {
              parent_template: (bundleMetadata as BundleToMetadata)
                ?.base_task_template_id,
            }
          : {};

        const deadlineDate = getDeadlineDate(deadline, approx_deadline);
        const payload = {
          entity_id: entity,
          season: season ? String(season) : null,
          product_content_type_id,
          product_id: uuid,
          quantity: 1,
          payment_amount: approx_price ? Number(approx_price) : 0,
          state_id,
          quarter,
          ...deadlineDate,
          ...taskName,
          ...bundleTo,
        };

        await addProductToCart({
          groupId: groupId,
          entityId: entity,
          payload: payload,
        }).unwrap();

        successToast({ message: "The filing has been added to the cart" });
        closeModal();
      } catch (e) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const handleSubmit = async (values: any) => {
    trackEvent(ADD_TASK_CLICKED, {
      screen: history.location.pathname,
      email,
      ...values,
    });

    try {
      const { entity, skipPaymentMode, ...rest } = values;

      if (skipPaymentMode === SKIP_PAYMENT_ADD_TO_CART) {
        handleSkipPaymentAddToCart(values);
        return;
      }

      const quarterNumber =
        data?.quarters?.findIndex(
          (quarter: any) => quarter === (values.quarter as string)
        ) || 0;
      const metadataFields =
        data?.metadata?.hasOwnProperty("fields") && "fields" in data.metadata
          ? (data?.metadata?.fields as unknown as BundleToMetadata[])
          : [];
      const bundleMetadata = metadataFields?.find(
        (data) =>
          (data as unknown as BundleToMetadata).type === "bundle_checkbox"
      );
      const bundleTo = values.bundle
        ? {
            parent_template: (bundleMetadata as unknown as BundleToMetadata)
              ?.base_task_template_id,
          }
        : {};

      const deadlineDate = getDeadlineDate(values.deadline);

      const invoiceDetails =
        skipPaymentMode === SKIP_PAYMENT_INVOICE_LATER
          ? {
              invoice_details: {
                amount: values.amount,
                description: values.description,
              },
            }
          : {};
      const task = await createTask({
        entity,
        metaData: {
          ...rest,
          quarter: quarterNumber + 1,
          ...bundleTo,
          ...deadlineDate,
          skip_payment_mode: skipPaymentMode || null,
          ...invoiceDetails,
        },
      }).unwrap();

      const { season, creator, title } = task || {};
      trackEvent(TASK_CREATED, {
        screen: history.location.pathname,
        creator,
        task_title: title,
        email,
        season,
        ...values,
      });
      if (skipPaymentMode === SKIP_PAYMENT_INVOICE_LATER) {
        successToast({
          title: "Successfully launched",
          message:
            "The filing has been launched and an invoice has been created",
        });
      } else {
        successToast({
          title: "Successfully launched",
          message: "The filing has been launched",
        });
      }

      if (redirectPathAfterCreate) {
        history.replace(`${redirectPathAfterCreate}/${task.uuid}${search}`);
        return;
      }
      history.replace(`/filings/${task.uuid}`);
    } catch (error) {
      alertToast({ message: (error as any)?.data?.error?.message });
      trackEvent(TASK_CREATION_FAILED, {
        screen: history.location.pathname,
        error,
        ...values,
      });
    }
  };
  const canUpdateDeadline = !isCustomer && data?.base_task_key === STATE_FILING;

  if (finalTemplateId === process.env.PUBLIC_AD_HOC_BASE_TASK_TEMPLATE_ID) {
    const adhocSeasonOptions = getSeasonsFrom(
      ADHOC_SEASON_MIN_YEAR,
      dayjs().year() + 1
    ).map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));

    return (
      <Modal.Root open onOpenChange={closeModal}>
        <FormikForm
          initialValues={{
            title: "",
            adhoc_scope_of_work: "",
            payment_amount: "",
            base_task_template_id: finalTemplateId,
            entity: "",
            deadline: "",
            season: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={addAdhocTaskSchema}
        >
          {({ setFieldValue, submitForm }) => (
            <Modal.Content>
              <Modal.Header>
                <div className="t-flex t-gap-1 t-items-center">
                  <Button
                    customType="ghost_icon"
                    onClick={() =>
                      handleClose ? handleClose() : history.goBack()
                    }
                    size="small"
                  >
                    <img
                      src={ArrowLeft}
                      alt="arrow left"
                      className="t-h-4 t-w—4"
                    />
                  </Button>
                  <Modal.Title>Adhoc Task</Modal.Title>
                </div>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <div className="add-edit-form t-flex t-flex-col t-gap-5">
                  <TextInput
                    block
                    name="title"
                    label="Filing name"
                    placeholder="Enter filing name"
                  />
                  <TextInput
                    block
                    name="adhoc_scope_of_work"
                    label="Scope of work"
                    placeholder="Enter scope of work"
                  />
                  <TextInput
                    block
                    name="payment_amount"
                    label="Price"
                    placeholder="Enter price"
                  />
                  <Combobox
                    components={{
                      ClearIndicator: () => null,
                    }}
                    menuPortalTarget={document.body}
                    name="season"
                    withForm
                    label="Season"
                    options={adhocSeasonOptions}
                    required
                  />
                  <DateField
                    name="deadline"
                    dateProps={{
                      label: "Deadline",
                      required: true,
                      onDateChange: (date: Date) =>
                        setFieldValue(
                          "deadline",
                          dayjs(date).format(YYYY_MM_DD)
                        ),
                      rightComponent: (
                        <CalendarIcon className="t-text-text-30 -t-mt-0.5" />
                      ),
                    }}
                  />
                  <Combobox
                    components={{
                      ClearIndicator: () => null,
                    }}
                    menuPortalTarget={document.body}
                    name="entity"
                    withForm
                    label="Entity"
                    options={entities?.map((entity) => ({
                      label: (
                        <EntityLabel
                          name={entity.name}
                          countryCode={entity.country_code}
                        />
                      ),
                      value: entity.uuid,
                    }))}
                    required
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  disabled={isLoading || disableLaunch}
                  isLoading={isLoading}
                  onClick={submitForm}
                >
                  Add Filing
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          )}
        </FormikForm>
      </Modal.Root>
    );
  }

  return (
    <Modal.Root open onOpenChange={closeModal}>
      <Modal.Content useCustomOverlay>
        {data ? (
          <>
            <FormikForm
              initialValues={{
                state_id: "",
                season: querySeason || "",
                base_task_template_id: finalTemplateId,
                entity: queryEntity || "",
                default_task_template: Boolean(data?.default_task_template),
                has_states: Boolean(data?.states),
                quarter: "",
                bundle: false,
                deadline: "",
                skipPaymentMode: isCustomer ? SKIP_PAYMENT_ADD_TO_CART : "",
                amount: data.approx_price || 0,
                description: "",
              }}
              onSubmit={(v) => {
                const { default_task_template, ...rest } = v;
                if (handleFormSubmit) {
                  handleFormSubmit(rest);
                } else {
                  handleSubmit(rest);
                }
              }}
              validationSchema={addTaskSchema}
              validationContext={{
                needDeadline: canUpdateDeadline,
              }}
            >
              {({ errors }) => {
                return (
                  <>
                    <Modal.Header>
                      <div className="t-flex t-gap-1 t-items-center">
                        <Button
                          customType="ghost_icon"
                          onClick={() =>
                            handleClose ? handleClose() : history.goBack()
                          }
                          size="small"
                        >
                          <img
                            src={ArrowLeft}
                            alt="arrow left"
                            className="t-h-4 t-w—4"
                          />
                        </Button>
                        <Modal.Title>
                          {typeof data.title === "string" ? data.title : ""}
                        </Modal.Title>
                      </div>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body className="t-max-h-[440] t-overflow-y-auto">
                      <TaskForm
                        templateId={finalTemplateId}
                        data={data as unknown as TaskFormData}
                        closeModal={closeModal}
                        disableLaunch={disableLaunch}
                        errors={errors}
                      />
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button type="button">Cancel</Button>
                      </Modal.RawClose>
                      <Button
                        customType="primary"
                        type="submit"
                        disabled={
                          isLoading || disableLaunch || addingProductToCart
                        }
                        isLoading={isLoading || addingProductToCart}
                      >
                        Add Filing
                      </Button>
                    </Modal.FooterButtonGroup>
                  </>
                );
              }}
            </FormikForm>
          </>
        ) : (
          <Loader />
        )}
      </Modal.Content>
    </Modal.Root>
  );
}
